import React from "react";
import { ElStyle } from "./state";
import { VarDef, varLabel, varSample } from "./emailVars";
import { buildChipHtml, decodeEntities } from "./chipDom";

// ---------- RENDER CONTEXT ----------

export interface RenderCtx {
	elStyle: Record<string, ElStyle>;
	theme: { brand: string; radius: number; font: string };
	sampleMode: boolean;
	catalog: VarDef[];
	/** When true, text fields get contentEditable + data-f + data-el; chips become atomic. */
	editable?: boolean;
}

// ---------- ELEMENT STYLE ----------

export function elStyleToReact(s: ElStyle | undefined): React.CSSProperties {
	if (!s) return {};
	const style: React.CSSProperties = {};
	if (s.size != null) style.fontSize = s.size + "px";
	if (s.color) style.color = s.color;
	if (s.bold != null) style.fontWeight = s.bold ? 800 : 400;
	if (s.capitalize) style.textTransform = "capitalize";
	if (s.align) style.textAlign = s.align as React.CSSProperties["textAlign"];
	if (s.bg) style.background = s.bg;
	if (s.radius != null) style.borderRadius = s.radius + "px";
	return style;
}

export function es(elStyle: Record<string, ElStyle>, key: string): React.CSSProperties {
	return elStyleToReact(elStyle[key]);
}

// ---------- VAR CHIP RENDERING ----------

/**
 * Splits text on ${varName} occurrences and returns an array of strings and
 * chip span elements. Read-only — no contentEditable.
 */
export function renderTextWithVars(text: string, ctx: RenderCtx): React.ReactNode[] {
	if (!text) return [];
	// Legacy content saved before entity-decoding may carry literal &nbsp;/&amp; — clean for display.
	const parts = decodeEntities(text).split(/\$\{([^}]+)\}/);
	const result: React.ReactNode[] = [];
	for (let i = 0; i < parts.length; i++) {
		if (i % 2 === 0) {
			// plain text segment
			if (parts[i]) result.push(parts[i]);
		} else {
			// variable name
			const name = parts[i];
			const display = ctx.sampleMode ? varSample(ctx.catalog, name) : varLabel(ctx.catalog, name);
			result.push(
				<span
					key={`var-${i}-${name}`}
					className={"var-chip" + (ctx.sampleMode ? " sample" : "")}
					data-var={name}
					title={"${" + name + "}"}>
					{display}
				</span>
			);
		}
	}
	return result;
}

/**
 * Editable variant: chips are contentEditable=false atomic spans; text segments are plain.
 * The parent element must be contentEditable=true.
 */
function renderTextWithVarsEditable(text: string, ctx: RenderCtx): React.ReactNode[] {
	if (!text) return [];
	const parts = text.split(/\$\{([^}]+)\}/);
	const result: React.ReactNode[] = [];
	for (let i = 0; i < parts.length; i++) {
		if (i % 2 === 0) {
			if (parts[i]) result.push(parts[i]);
		} else {
			const name = parts[i];
			const display = ctx.sampleMode ? varSample(ctx.catalog, name) : varLabel(ctx.catalog, name);
			result.push(
				<span
					key={`var-${i}-${name}`}
					className={"var-chip" + (ctx.sampleMode ? " sample" : "")}
					contentEditable={false}
					data-var={name}
					title={"${" + name + "}"}>
					{display}
				</span>
			);
		}
	}
	return result;
}

/**
 * Returns the appropriate text renderer nodes depending on ctx.editable.
 */
export function renderText(text: string, ctx: RenderCtx): React.ReactNode[] {
	return ctx.editable ? renderTextWithVarsEditable(text, ctx) : renderTextWithVars(text, ctx);
}

function escapeHtml(s: string): string {
	return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/**
 * Build the inner HTML string for an editable field (text + atomic var-chips).
 * Editable fields render their content via dangerouslySetInnerHTML so React does NOT
 * manage the contentEditable's children — direct DOM edits (typing, drag, execCommand)
 * then can't desync the vDOM and crash with "removeChild: node is not a child".
 */
export function editableHtml(text: string, ctx: RenderCtx): string {
	if (!text) return "";
	// Legacy content saved before entity-decoding may carry literal &nbsp;/&amp; — clean,
	// then escapeHtml re-escapes properly for the innerHTML string.
	const parts = decodeEntities(text).split(/\$\{([^}]+)\}/);
	let html = "";
	for (let i = 0; i < parts.length; i++) {
		if (i % 2 === 0) {
			if (parts[i]) html += escapeHtml(parts[i]);
		} else {
			const name = parts[i];
			const display = ctx.sampleMode ? varSample(ctx.catalog, name) : varLabel(ctx.catalog, name);
			html += buildChipHtml(name, escapeHtml(display), ctx.sampleMode);
		}
	}
	return html;
}

/**
 * Spreadable content props for a text field: editable → dangerouslySetInnerHTML (opaque
 * to React); read-only → React children. Pass instead of putting renderText() as a child.
 */
export function contentProps(text: string, ctx: RenderCtx): React.HTMLAttributes<HTMLElement> {
	if (ctx.editable) {
		// admin-authored email-template markup rendered in the admin-only builder preview
		return { dangerouslySetInnerHTML: { __html: editableHtml(text, ctx) } }; // nosemgrep
	}
	return { children: renderTextWithVars(text, ctx) };
}

/**
 * Returns props for an editable text field element.
 * data-el encodes  blockId:field[:rowIndex]  for blur write-back in BlockShell.
 */
export function editableProps(
	dataEl: string,
	field: string,
	extra?: React.HTMLAttributes<HTMLElement>
): React.HTMLAttributes<HTMLElement> & { "data-el": string; "data-f": string; contentEditable: true } {
	return {
		...extra,
		contentEditable: true,
		suppressContentEditableWarning: true,
		"data-el": dataEl,
		"data-f": field,
	} as React.HTMLAttributes<HTMLElement> & { "data-el": string; "data-f": string; contentEditable: true };
}
